import type { HeightMap, Point, RiverFeature, WorldGeneratorConfig } from './types';
import { SeededRandom } from './SeededRandom';

export class RiverGenerator {
  private readonly random: SeededRandom;

  constructor(private readonly config: WorldGeneratorConfig) {
    this.random = new SeededRandom(config.seed.seed).fork('river');
  }

  generateRiver(heightMap: HeightMap): RiverFeature {
    const source = this.findHighSource(heightMap);
    const mouth = this.findMouth(source);
    const path = this.meander(source, mouth);
    return {
      id: 'main-river',
      kind: 'river',
      source,
      mouth,
      path,
      width: 1,
    };
  }

  private findHighSource(heightMap: HeightMap): Point {
    let best: Point = { x: this.config.width - 7, y: 1 };
    let bestHeight = -Infinity;
    for (let y = 1; y < Math.min(6, this.config.height); y++) {
      for (let x = Math.floor(this.config.width * 0.68); x < this.config.width - 2; x++) {
        const height = heightMap.values[y * this.config.width + x] ?? 0;
        if (height > bestHeight) {
          bestHeight = height;
          best = { x, y };
        }
      }
    }
    return { x: clamp(Math.floor(this.config.width * 0.72), this.config.width - 3, best.x), y: 0 };
  }

  private findMouth(source: Point): Point {
    return {
      x: clamp(1, this.config.width - 2, source.x - 4),
      y: this.config.height - 1,
    };
  }

  private meander(source: Point, mouth: Point): readonly Point[] {
    const baseX = clamp(Math.floor(this.config.width * 0.64), this.config.width - 5, source.x + this.random.int(-1, 1));
    const anchors: Point[] = [
      source,
      this.safePoint(baseX + this.random.int(1, 3), 4),
      this.safePoint(baseX + this.random.int(-2, 0), 8),
      this.safePoint(baseX + this.random.int(0, 2), 12),
      this.safePoint(baseX + this.random.int(-3, -1), 16),
      mouth,
    ];

    return this.uniqueConsecutive(this.pathThroughAnchors(anchors));
  }

  private pathThroughAnchors(anchors: readonly Point[]): readonly Point[] {
    const points: Point[] = [];
    for (let index = 0; index < anchors.length - 1; index++) {
      const from = anchors[index];
      const to = anchors[index + 1];
      let x = from.x;
      let y = from.y;
      points.push(this.safePoint(x, y));

      let safety = 0;
      while ((x !== to.x || y !== to.y) && safety < 80) {
        safety++;
        const remainingX = Math.abs(to.x - x);
        const remainingY = Math.abs(to.y - y);
        const stepX = Math.sign(to.x - x);
        const stepY = Math.sign(to.y - y);

        if (remainingX > 0 && (remainingX >= remainingY || safety % 3 === 0)) {
          x += stepX;
          points.push(this.safePoint(x, y));
        }

        if (y !== to.y) {
          y += stepY;
          points.push(this.safePoint(x, y));
        }
      }
    }
    return points;
  }

  private safePoint(x: number, y: number): Point {
    const avoidsVillageCore = y >= 8 && y <= 12;
    const minX = avoidsVillageCore ? Math.floor(this.config.width * 0.66) : 1;
    return {
      x: clamp(minX, this.config.width - 2, x),
      y: clamp(0, this.config.height - 1, y),
    };
  }

  private removeBacktracks(points: readonly Point[]): readonly Point[] {
    const out: Point[] = [];
    for (const point of points) {
      const previous = out[out.length - 2];
      const last = out[out.length - 1];
      if (previous && last && previous.x === point.x && previous.y === point.y) {
        out.pop();
        continue;
      }
      out.push(point);
    }
    return out;
  }

  private uniqueConsecutive(points: readonly Point[]): readonly Point[] {
    const out: Point[] = [];
    for (const point of points) {
      const last = out[out.length - 1];
      if (last?.x === point.x && last.y === point.y) continue;
      out.push(point);
    }
    return this.removeBacktracks(out);
  }
}

function clamp(min: number, max: number, value: number): number {
  return Math.max(min, Math.min(max, value));
}
